import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Zap, DollarSign, Target, BarChart3, Sparkles } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function PricingIntelligencePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [priceComparison, setPriceComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileData, productsData] = await Promise.all([
        authService.getProfile(),
        apiClient.get('/api/dashboard/products', { params: { limit: 100 } })
      ]);
      setUser(profileData.member);
      setProducts(productsData.data.products || []);
    } catch (error) {
      console.error('Failed to load:', error);
      navigate('/login');
    }
  };

  const analyzeProductPricing = async (product) => {
    setSelectedProduct(product);
    setAnalyzing(true);
    
    try {
      const response = await apiClient.post('/api/product-intelligence/compare', {
        product_name: product.title,
        current_price: product.price,
        current_url: `${window.location.origin}/product/${product._id}`
      });
      
      setPriceComparison(response.data);
      toast.success('Pricing analysis complete!', {
        description: `Found ${response.data.retailers?.length || 0} competitor prices`
      });
    } catch (error) {
      toast.error('Analysis failed', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const getCIColor = (score) => {
    if (score >= 800) return 'text-[#3EF1C7] bg-[#3EF1C7]/10';
    if (score >= 600) return 'text-[#8CF78E] bg-[#8CF78E]/10';
    if (score >= 300) return 'text-[#FFC857] bg-[#FFC857]/10';
    return 'text-[#F55454] bg-[#F55454]/10';
  };

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Pricing Intelligence</h1>
            <p className="text-sm text-[#9CB0C7]">Pricing Intelligence</p>
            <p className="text-xs text-[#7A5CFF]">Pro Plus Feature - AI-Powered</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">← Dashboard</button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-8 h-8 text-[#7A5CFF]" />
            <h2 className="text-3xl font-bold text-white">AI-Powered Pricing Intelligence</h2>
          </div>
          <p className="text-[#9CB0C7]">Analyze your pricing against competitors across the web • Deep research • A/B testing • Strategic insights</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product Selector */}
          <div className="lg:col-span-1">
            <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 sticky top-6">
              <h3 className="text-xl font-semibold text-white mb-4">Select Product to Analyze</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {products.map((product) => (
                  <button
                    key={product._id}
                    onClick={() => analyzeProductPricing(product)}
                    disabled={analyzing}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedProduct?._id === product._id
                        ? 'border-[#7A5CFF] bg-[#7A5CFF]/10'
                        : 'border-[#22304A] bg-[#111A2E] hover:border-[#7A5CFF]/40'
                    } disabled:opacity-50`}
                  >
                    <div className="text-sm font-semibold text-white mb-1">{product.title}</div>
                    <div className="text-xs text-[#9CB0C7]">${product.price} • CI: {product.ci_score}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Analysis Results */}
          <div className="lg:col-span-2">
            {analyzing ? (
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-12 text-center">
                <Sparkles className="w-16 h-16 text-[#7A5CFF] mx-auto mb-4 animate-pulse" />
                <h3 className="text-xl font-semibold text-white mb-2">Analyzing Pricing Across Web...</h3>
                <p className="text-[#9CB0C7]">Using AI to search and extract pricing data</p>
              </div>
            ) : !priceComparison ? (
              <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-12 text-center">
                <Target className="w-16 h-16 text-[#9CB0C7] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Select a product to analyze</h3>
                <p className="text-[#9CB0C7]">Get competitive pricing intelligence powered by AI</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary Card */}
                <div className="rounded-lg border border-[#7A5CFF] bg-gradient-to-r from-[#7A5CFF]/20 to-[#3EF1C7]/10 backdrop-blur p-6">
                  <h3 className="text-2xl font-bold text-white mb-4">{priceComparison.product}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[#9CB0C7] mb-1">CI Score Range</p>
                      <p className="text-3xl font-bold text-white">{priceComparison.ci_score_range.min} - {priceComparison.ci_score_range.max}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CB0C7] mb-1">Best Deal</p>
                      <p className="text-xl font-bold text-[#3EF1C7]">{priceComparison.best_deal?.site}</p>
                      <p className="text-sm text-white">${priceComparison.best_deal?.price} • CI: {priceComparison.best_deal?.ci_score}</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-[#0E1426]/60 rounded-lg">
                    <p className="text-sm text-[#9CB0C7]">
                      <span className="text-[#3EF1C7] font-semibold">💡 Recommendation:</span> {priceComparison.recommendation}
                    </p>
                  </div>
                </div>

                {/* Competitor Prices Table */}
                <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden">
                  <div className="p-4 border-b border-[#22304A] bg-[#111A2E]">
                    <h4 className="font-semibold text-white">Competitor Pricing Across Web</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#111A2E]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Retailer</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">CI Score</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#22304A]">
                        {priceComparison.retailers?.map((retailer, idx) => (
                          <tr key={idx} className="hover:bg-[#111A2E]/40">
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-white">{retailer.site}</div>
                              {idx === 0 && <span className="text-xs text-[#3EF1C7]">🏆 Best Deal</span>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-lg font-bold text-white">${retailer.price.toFixed(2)}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${getCIColor(retailer.ci_score)}`}>
                                {retailer.ci_score}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => window.open(retailer.url, '_blank')}
                                className="text-xs text-[#3EF1C7] hover:text-[#3EF1C7]/80"
                              >
                                View →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
